import { useEffect, useRef, useState } from 'react';
import { ApiError } from './api.js';
// Standard list/detail fetch with explicit loading + error state. Replaces the
// ad-hoc `api.get(...).then(setX).catch(() => {})` pattern that silently turned
// a network failure into "empty list" — so a down API now shows a real error +
// retry instead of looking like there's no data.
//
// ⚠️ `deps` must be PRIMITIVES (string / number / boolean) or otherwise
// referentially stable values — same contract as a useEffect dep array. They are
// spread straight into the effect's dependency list, so passing an inline object
// or array (e.g. `[{ filter: true }]`) fails React's identity check every render
// and refetches in a loop. Pass `orgId`, not `{ orgId }`. A guard below warns if
// it spots a non-primitive dep (once per hook instance — it only fires on a
// developer error, so it stays on in production builds too).
//
// SCALE: swap for @tanstack/react-query (or swr) once lists need caching,
// request dedup, or background revalidation. This hook is the deliberate
// "customer 1 light" seam, not the end state.
export function useResource(fetcher, deps = []) {
    const [state, setState] = useState({
        loading: true,
        error: null,
        revision: 0,
    });
    const [nonce, setNonce] = useState(0);
    const requestRef = useRef({
        deps: [],
        nonce: -1,
        revision: 0,
    });
    const requestChanged = requestRef.current.nonce !== nonce
        || requestRef.current.deps.length !== deps.length
        || deps.some((value, index) => !Object.is(value, requestRef.current.deps[index]));
    if (requestChanged) {
        requestRef.current = {
            deps: [...deps],
            nonce,
            revision: requestRef.current.revision + 1,
        };
    }
    const requestRevision = requestRef.current.revision;
    // Footgun guard: a non-primitive dep is almost always a mistake that causes
    // an infinite refetch loop.
    const warnedRef = useRef(false);
    if (!warnedRef.current) {
        const bad = deps.some((d) => d !== null && (typeof d === 'object' || typeof d === 'function'));
        if (bad) {
            warnedRef.current = true;
            // eslint-disable-next-line no-console
            console.warn('[useResource] non-primitive dependency detected. Deps must be primitives ' +
                '(string/number/boolean); an object/array/function changes identity every ' +
                'render and will refetch in a loop. Deps:', deps);
        }
    }
    useEffect(() => {
        let cancelled = false;
        setState((s) => ({ ...s, loading: true, error: null }));
        fetcher()
            .then((d) => {
            if (!cancelled)
                setState({ data: d, loading: false, error: null, revision: requestRevision });
        })
            .catch((e) => {
            if (cancelled)
                return;
            setState((s) => ({
                data: s.data,
                loading: false,
                error: e instanceof ApiError ? e.message : 'Failed to load',
                revision: s.revision,
            }));
        });
        return () => { cancelled = true; };
        // The fetcher is intentionally not a dep — callers express invalidation via `deps`.
    }, [...deps, nonce]);
    return {
        data: state.data, loading: state.loading, error: state.error,
        revision: state.revision, requestRevision,
        reload: () => setNonce((n) => n + 1),
    };
}
